import React from "react"
import { observer } from "mobx-react-lite"
import {
  View,
  ViewStyle,
  TextStyle,
  Dimensions,
  Linking,
  ListRenderItem,
  TouchableOpacity,
  FlatList
} from "react-native"
import { StackScreenProps } from "@react-navigation/stack"
import { TabsScreenProps } from "@app/navigators"
import { Button, Screen, Text, Svg } from "@app/components"
import { TextProps } from "@app/components/text/text.props"
import { color, spacing } from "@app/theme"
import { Footer } from "@app/components/footer/footer"
import { Feather } from "@expo/vector-icons"
import { textPresets } from "@app/components/button/button.presets"
import { useNavigation } from "@react-navigation/native"
import { RootNavigatorParamList } from "@app/navigators/navigator-params"

type ListItem = {
  text: any
  preset: TextProps["preset"]
}

const SUPPORT_EMAIL = "kundtjanst@veckanse.com"

type TermsScreenProps = StackScreenProps<RootNavigatorParamList, "Terms">
export const TermsScreen = observer((props: TermsScreenProps) => {
  const goHome = () =>
    props.navigation.navigate("Tabs", { screen: "Home", params: { screen: "Offers" } })
  const navigation = useNavigation<TabsScreenProps["navigation"]>()

  const renderItem: ListRenderItem<ListItem> = ({ item }) => {
    switch (item.preset) {
      case "bold":
        return (
          <Text preset={item.preset} style={LIST_ITEM_BOLD}>
            {item.text}
          </Text>
        )
      default:
        return (
          <Text preset={item.preset} style={LIST_ITEM_DEFAULT}>
            {item.text}
          </Text>
        )
    }
  }

  const mailLink = (
    <TouchableOpacity onPress={() => Linking.openURL(`mailto:${SUPPORT_EMAIL}`)}>
      <Text style={LINK} text={SUPPORT_EMAIL} />
    </TouchableOpacity>
  )

  return (
    <Screen style={ROOT} preset="scroll">
      <View style={PAGE_WRAPPER}>
        <Button preset="back" onPress={goHome}>
          <Feather style={textPresets.back} name="chevron-left" />
          <Text style={textPresets.back} text="Tillbaka" />
        </Button>
        <View style={BACKGROUND_IMAGE}>
          <Svg preset="PinkBlob" />
        </View>
        <Text style={HEADER_LARGE} preset="headerLarge" text="Användarvillkor" />
        <Text preset="default" text="Villkor för användning av VeckansEs tjänster" />
        <Text
          preset="default"
          text="Cartero Digital Marketing AB | Org.nr 556857–4262 | Box 5077, 102 42 Stockholm"
        />
        <Text preset="default" text="Gäller från: 2026-04-20" />

        <FlatList
          data={[
            { preset: "bold", text: "1. Inledning och tillämpningsområde" },
            {
              preset: "default",
              text: "Dessa användarvillkor ('Villkoren') gäller för din användning av tjänster som tillhandahålls av Cartero Digital Marketing AB, org.nr 556857–4262 ('VeckansE', 'vi' eller 'oss'). Med tjänster avses VeckansEs webbplats (veckanse.se), nyhetsbrev, applikationer och övriga produkter."
            },
            {
              preset: "default",
              text: "Genom att prenumerera på VeckansEs nyhetsbrev eller använda veckanse.se godkänner du dessa villkor. Om du inte godkänner villkoren ska du inte använda tjänsterna."
            },
            { preset: "bold", text: "2. Behörighet att använda tjänsterna" },
            {
              preset: "default",
              text: "Du måste ha fyllt 18 år och ha rättslig kapacitet att ingå avtal enligt svensk lag för att använda våra tjänster. Genom att acceptera villkoren bekräftar du att du uppfyller dessa krav."
            },
            {
              preset: "default",
              text: "VeckansE riktar sig inte till minderåriga och vi samlar inte medvetet in personuppgifter om personer under 18 år."
            },
            { preset: "bold", text: "3. Registrering och prenumeration" },
            {
              preset: "default",
              text: "För att prenumerera på VeckansEs nyhetsbrev anger du din e-postadress och aktivt bekräftar din prenumeration. Vi skickar ett bekräftelsemejl (double opt-in) som du måste klicka på för att aktivera prenumerationen."
            },
            {
              preset: "default",
              text: "Du ansvarar för att de uppgifter du lämnar är korrekta och aktuella."
            },
            { preset: "bold", text: "4. Tillhandahållande av tjänster" },
            {
              preset: "default",
              text: "VeckansE tillhandahåller ett kostnadsfritt nyhetsbrev med erbjudanden och deals från svenska och internationella butiker och varumärken. Vi förbehåller oss rätten att uppdatera, förändra eller begränsa tjänsterna. Vid väsentliga ändringar informerar vi dig."
            },
            {
              preset: "default",
              text: "VeckansE ansvarar inte för innehåll på tredjepartswebbplatser som länkas till från våra utskick eller vår webbplats."
            },
            { preset: "bold", text: "5. Marknadsföring och kommunikation" },
            {
              preset: "default",
              text: "VeckansE kommunicerar med dig via e-post baserat på ditt samtycke. Vi kan även kommunicera via SMS och push-notiser om du uttryckligen samtyckt till detta."
            },
            {
              preset: "default",
              text: "Du kan när som helst avregistrera dig från VeckansEs utskick kostnadsfritt via:"
            },
            { preset: "default", text: "• Avregistreringslänken i varje utskick" },
            {
              preset: "default",
              text: (
                <>
                  • E-post till {mailLink}
                </>
              )
            },
            {
              preset: "default",
              text: "Avregistrering från VeckansEs nyhetsbrev påverkar inte din eventuella direktrelation med tredje parter (t.ex. Smart Senior AB) som du separat valt att ta kontakt med."
            },
            { preset: "bold", text: "6. Delning av uppgifter med samarbetspartners" },
            {
              preset: "default",
              text: (
                <>
                  VeckansE delar personuppgifter med Smart Senior AB inom ramen för ett
                  samarbetsavtal. Fullständig information om vad som delas, med vem och för vilket
                  ändamål finns i vår{" "}
                  <TouchableOpacity onPress={() => navigation.navigate("Policy")}>
                    <Text style={LINK} text="integritetspolicy" />
                  </TouchableOpacity>
                  .
                </>
              )
            },
            {
              preset: "default",
              text: (
                <>
                  Smart Senior AB är en separat personuppgiftsansvarig. Om du inte vill att dina
                  uppgifter delas med Smart Senior AB kan du invända mot detta genom att kontakta
                  oss på {mailLink}
                </>
              )
            },
            { preset: "bold", text: "7. Användares skyldigheter" },
            { preset: "default", text: "Du åtar dig att:" },
            { preset: "default", text: "• Tillhandahålla korrekta uppgifter vid registrering" },
            {
              preset: "default",
              text: "• Inte använda tjänsterna på ett sätt som stör eller skadar VeckansE eller andra användare"
            },
            {
              preset: "default",
              text: "• Inte kopiera, sälja eller distribuera VeckansEs innehåll utan tillstånd"
            },
            { preset: "default", text: "• Inte försöka kringgå tekniska skyddsåtgärder" },
            { preset: "bold", text: "8. Immateriella rättigheter" },
            {
              preset: "default",
              text: "Allt innehåll på veckanse.se och i VeckansEs utskick — inklusive texter, bilder, logotyper och design — tillhör Cartero Digital Marketing AB eller används med tillstånd från rättighetsinnehavare. Du får inte reproducera eller använda innehållet utan skriftligt tillstånd."
            },
            { preset: "bold", text: "9. Ansvarsbegränsning" },
            {
              preset: "default",
              text: "VeckansE tillhandahåller tjänsterna i befintligt skick. Vi ansvarar inte för indirekta skador, utebliven vinst eller skador som uppstår till följd av din användning av tjänsterna eller tillit till information i våra utskick."
            },
            {
              preset: "default",
              text: "VeckansE ansvarar inte för priser, villkor eller tillgänglighet hos de butiker och varumärken vars erbjudanden presenteras."
            },
            { preset: "bold", text: "10. GDPR och personuppgifter" },
            {
              preset: "default",
              text: (
                <>
                  Personuppgifter behandlas i enlighet med GDPR och vår integritetspolicy. Du har
                  rättigheter avseende dina personuppgifter — se{" "}
                  <TouchableOpacity onPress={() => navigation.navigate("Policy")}>
                    <Text style={LINK} text="integritetspolicyn" />
                  </TouchableOpacity>{" "}
                  för fullständig information.
                </>
              )
            },
            { preset: "bold", text: "11. Ändringar av villkoren" },
            {
              preset: "default",
              text: "Vi kan uppdatera dessa villkor. Vid väsentliga ändringar informerar vi dig via e-post med minst 30 dagars varsel. Fortsatt användning av tjänsterna efter en uppdatering innebär att du godkänner de nya villkoren. Aktuell version finns alltid på veckanse.se/anvandarvillkor."
            },
            { preset: "bold", text: "12. Tillämplig lag och tvister" },
            {
              preset: "default",
              text: "Dessa villkor regleras av svensk lag. Tvister ska i första hand lösas genom förhandling. Om parterna inte kan enas ska tvisten avgöras av svensk allmän domstol med Stockholms tingsrätt som första instans."
            },
            {
              preset: "default",
              text: "Som konsument kan du även vända dig till Allmänna reklamationsnämnden (ARN), arn.se."
            },
            { preset: "bold", text: "13. Kontakt" },
            { preset: "default", text: "Cartero Digital Marketing AB" },
            { preset: "default", text: "Box 5077, 102 42 Stockholm" },
            { preset: "default", text: "Org.nr: 556857–4262" },
            {
              preset: "default",
              text: (
                <>
                  E-post: {mailLink}
                </>
              )
            },
            { preset: "default", text: "Webbplats: veckanse.se" }
          ]}
          renderItem={renderItem}
        />
      </View>
      <Footer />
    </Screen>
  )
})

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  minHeight: Dimensions.get("window").height
}

const LIST_ITEM_BOLD: ViewStyle = {
  marginTop: spacing[4],
  marginBottom: spacing[2]
}
const LIST_ITEM_DEFAULT: ViewStyle = {
  marginLeft: spacing[3],
  marginBottom: spacing[2]
}

const PAGE_WRAPPER: ViewStyle = {
  padding: 24,
  paddingBottom: 80,
  maxWidth: 840,
  minHeight: Dimensions.get("window").height,
  alignSelf: "center"
}

const BACKGROUND_IMAGE: ViewStyle = {
  height: 500,
  width: 580,
  position: "absolute",
  top: -100,
  left: -200,
  transform:
    Dimensions.get("window").width > 768
      ? [{ scale: 2 }, { rotate: "-245deg" }]
      : [{ scale: 1 }, { rotate: "-245deg" }],
  zIndex: -1
}

const HEADER_LARGE: TextStyle = {
  marginTop: spacing[5],
  marginBottom: spacing[5]
}

const LINK: TextStyle = {
  color: color.palette.pink3
}
